/**
 * Die Trainingsdaten, die Auswertung, Laufen, Körperdaten und der Überblick
 * des Bereichs "Gesundheit & Körper" gemeinsam brauchen - einmal geladen,
 * damit jede Seite dieselben Zahlen zeigt.
 */
import { useMemo } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';

import { db } from '@/db/schema';
import { alleRaenge } from '@/domain/training/rang';
import { einheitenAm, planAm } from '@/domain/training/plan';
import { useAlleEinheiten, useBibliothekBereit, usePlaene, useUebungsMap } from '@/modules/training/useTraining';
import { today } from '@/lib/datetime';

export function useTrainingDaten() {
  useBibliothekBereit();
  const heute = today();
  const protokolleGeladen = useLiveQuery(() => db.trainingsprotokolle.toArray(), []);
  const protokolle = useMemo(() => protokolleGeladen ?? [], [protokolleGeladen]);
  const geladen = usePlaene();
  const plaene = useMemo(() => geladen ?? [], [geladen]);
  const einheiten = useAlleEinheiten();
  const bibliothek = useUebungsMap();
  // `null` = geladen, aber noch nicht angelegt; `undefined` = laedt noch.
  const einstellungenRoh = useLiveQuery(async () => (await db.trainingsEinstellungen.get('standard')) ?? null, []);
  const einstellungen = einstellungenRoh ?? undefined;
  const gewichteGeladen = useLiveQuery(() => db.koerpergewicht.toArray(), []);
  const gewichte = useMemo(() => gewichteGeladen ?? [], [gewichteGeladen]);
  const koerpergewicht = useMemo(() => [...gewichte].sort((a, b) => a.datum.localeCompare(b.datum)).at(-1)?.kg ?? null, [gewichte]);
  const raenge = useMemo(
    () => alleRaenge({ protokolle, bibliothek, heute, koerpergewicht, ziele: einstellungen?.muskelZiele, standards: einstellungen?.kraftStandards }),
    [protokolle, bibliothek, heute, koerpergewicht, einstellungen?.muskelZiele, einstellungen?.kraftStandards],
  );
  const planHeute = useMemo(() => {
    const plan = planAm(plaene, heute);
    return {
      geplant: plan ? einheitenAm(plan, einheiten, heute) : [],
      einheiten: plan ? einheiten.filter((e) => e.planId === plan.id).sort((a, b) => a.wochentag - b.wochentag || a.order - b.order) : [],
    };
  }, [plaene, einheiten, heute]);
  const bereit = protokolleGeladen !== undefined && einstellungenRoh !== undefined && gewichteGeladen !== undefined && bibliothek.size > 0;
  return { heute, protokolle, plaene, einheiten, bibliothek, einstellungen, gewichte, koerpergewicht, raenge, planHeute, bereit };
}
